import { useState, type ReactNode } from 'react'
import type { Locale } from '@diana/shared'
import { api } from '../../lib/api.js'
import { useContent } from '../../hooks/useContent.js'
import { useLocale } from '../../i18n/index.js'

/**
 * Lands in Diana's panel — no Google Forms, no third party holding her leads.
 *
 * Phase 1b replaces this with a real booking calendar; the lead form stays
 * for people who aren't ready to book yet.
 */

type PageStrings = {
  eyebrow: ReactNode
  title: string
  doneTitle: string
  doneBody: string
  nameLabel: string
  emailLabel: string
  messageLabel: string
  messagePlaceholder: string
  sending: string
  submit: string
  error: string
}

const T: Record<Locale, PageStrings> = {
  pl: {
    eyebrow: (
      <>
        Napisz <span className="text-gold-600">do mnie</span>
      </>
    ),
    title: 'Zacznijmy od rozmowy',
    doneTitle: 'Dziękuję — wiadomość dotarła.',
    doneBody: 'Odpiszę osobiście, zwykle w ciągu 24 godzin.',
    nameLabel: 'Jak masz na imię?',
    emailLabel: 'Adres e-mail',
    messageLabel: 'O czym chcesz porozmawiać?',
    messagePlaceholder: 'Jaki masz poziom, po co uczysz się polskiego, co Cię blokuje…',
    sending: 'Wysyłam…',
    submit: 'Wyślij wiadomość',
    error: 'Nie udało się wysłać. Spróbuj jeszcze raz.',
  },
  uk: {
    eyebrow: (
      <>
        Напиши <span className="text-gold-600">мені</span>
      </>
    ),
    title: 'Почнімо з розмови',
    doneTitle: 'Дякую — повідомлення дійшло.',
    doneBody: 'Відповім особисто, зазвичай протягом 24 годин.',
    nameLabel: 'Як тебе звати?',
    emailLabel: 'Електронна пошта',
    messageLabel: 'Про що хочеш поговорити?',
    messagePlaceholder: 'Який у тебе рівень, навіщо вчиш польську, що тебе гальмує…',
    sending: 'Надсилаю…',
    submit: 'Надіслати повідомлення',
    error: 'Не вдалося надіслати. Спробуй ще раз.',
  },
  ru: {
    eyebrow: (
      <>
        Напиши <span className="text-gold-600">мне</span>
      </>
    ),
    title: 'Начнём с разговора',
    doneTitle: 'Спасибо — сообщение дошло.',
    doneBody: 'Отвечу лично, обычно в течение 24 часов.',
    nameLabel: 'Как тебя зовут?',
    emailLabel: 'Электронная почта',
    messageLabel: 'О чём хочешь поговорить?',
    messagePlaceholder: 'Какой у тебя уровень, зачем учишь польский, что тебя тормозит…',
    sending: 'Отправляю…',
    submit: 'Отправить сообщение',
    error: 'Не удалось отправить. Попробуй ещё раз.',
  },
}

export function ContactPage() {
  const locale = useLocale()
  const s = T[locale]
  const { data } = useContent(locale)
  const [form, setForm] = useState({ name: '', email: '', message: '' })
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle')

  const contact = data?.settings.contact as { responseTime?: string } | undefined

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setState('sending')
    try {
      await api.sendLead({ ...form, locale })
      setState('done')
    } catch {
      setState('error')
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-5 py-16">
      <h2 className="h-display text-2xl italic sm:text-3xl">{s.eyebrow}</h2>
      <h1 className="h-display mt-3 text-3xl sm:text-4xl">{s.title}</h1>
      {contact?.responseTime && <p className="mt-5 text-lg leading-7 font-light">{contact.responseTime}</p>}

      {state === 'done' ? (
        <div className="mt-10 rounded-sm bg-sky-100 p-9 text-center">
          <p className="h-display text-2xl italic">{s.doneTitle}</p>
          <p className="mt-3 font-light">{s.doneBody}</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mt-10 space-y-5">
          <Field label={s.nameLabel} htmlFor="name">
            <input
              id="name"
              required
              maxLength={120}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="field"
            />
          </Field>

          <Field label={s.emailLabel} htmlFor="email">
            <input
              id="email"
              type="email"
              required
              maxLength={200}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="field"
            />
          </Field>

          <Field label={s.messageLabel} htmlFor="message">
            <textarea
              id="message"
              required
              rows={6}
              maxLength={4000}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder={s.messagePlaceholder}
              className="field"
            />
          </Field>

          <button type="submit" disabled={state === 'sending'} className="btn btn-gold disabled:opacity-60">
            {state === 'sending' ? s.sending : s.submit}
          </button>

          {state === 'error' && <p className="text-sm text-rose">{s.error}</p>}
        </form>
      )}
    </div>
  )
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string
  htmlFor: string
  children: React.ReactNode
}) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="mb-2 block font-roboto text-xs font-bold uppercase tracking-widest text-ink-faint"
      >
        {label}
      </label>
      {children}
    </div>
  )
}
